import React, { useState } from 'react'

import { Box, Link, Typography } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'

import { MAX_AMOUNT_OF_PICS, MIN_AMOUNT_OF_PICS, MIN_PRICE } from 'common/utils/conciergeDigitalInvoiceItem.constants'
import ContainedSection from 'artkive/components/PageSections/ContainedSection'
import ProductDescriptionItem from 'artkive/components/PageSections/ProductDescriptionItem'
import ProductPriceTypography from 'artkive/components/PageSections/ProductPriceTypography'
import Testimonial from 'artkive/components/sections/Testimonial'
import { TrackButton } from 'artkive/components/Tracker'
import { DIGITAL_STORAGE, GET_BOX_CLICK } from 'artkive/constants/tracker/mainNavigation'
import withApp from 'artkive/hoc/withApp'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import useSale from 'artkive/hooks/useSale'
import useScrollToSlider from 'artkive/hooks/useScrollToSlider'
import PriceSlider from 'artkive/pages/Books/PriceSlider'
import ValueLabelComponent from 'artkive/pages/Digital/ValueLabelComponent'
import ProductDescriptionSection from 'artkive/pages/GetStarted/ProductDescriptionSection'
import { HELP_EMAIL } from 'artkive/utils/company'
import { PRICING } from 'artkive/utils/constants'
import theme from 'artkive/utils/theme'

import TestimonialImage from 'images/ecom/digital/Testimonial.png'
import DigitalStorageImg from 'images/ecom/pricing/DigitalStorage.png'
import DigitalStorageMobileImg from 'images/ecom/pricing/DigitalStorage-mobile.png'
import DigitalStorageMobileImgX2 from 'images/ecom/pricing/DigitalStorage-mobile-x2.png'
import DigitalStorageImgX2 from 'images/ecom/pricing/DigitalStorage-x2.png'

import { digitalBullets, testimonialUser } from './Digital.constants'

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    overflowX: 'hidden',
    backgroundColor: theme.colors.sky[100],
  },
  description: {
    [theme.breakpoints.up('xl')]: {
      maxWidth: '640px',
    },
  },
  priceInfoBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    gap: '8px',
    marginTop: '24px',
    [theme.breakpoints.up('md')]: {
      alignItems: 'flex-start',
    },
  },
}))

const Digital = () => {
  const classes = useStyles()
  const isMobile = useMobileBreakpoint()
  const muiTheme = useTheme()
  const [sliderValue, setSliderValue] = useState(MIN_AMOUNT_OF_PICS)
  const sliderRef = useScrollToSlider()
  const { boxCheckoutUrl } = useSale()

  const handleSliderChange = (e, value) => setSliderValue(value)

  return (
    <div className={classes.root}>
      <ProductDescriptionSection
        image={
          <picture>
            <source srcSet={`${DigitalStorageImgX2}`} media={muiTheme.breakpoints.up('lg').replace('@media ', '')} />
            <source
              srcSet={`${DigitalStorageImg}, ${DigitalStorageImgX2} 2x`}
              media={muiTheme.breakpoints.up('md').replace('@media ', '')}
            />
            <img
              srcSet={`${DigitalStorageMobileImg}, ${DigitalStorageMobileImgX2} 2x`}
              src={DigitalStorageMobileImg}
              alt='Account application preview'
            />
          </picture>
        }
        imageStyle='contain'
        productBg='#FFF0CC'
        descriptionBg={theme.colors.sky[300]}
        descriptionClassName={classes.description}
        withBubbles
      >
        <Typography variant='h1' align={isMobile ? 'center' : 'left'}>
          Art Digitization
        </Typography>
        <Box mb={3} mt={2}>
          <Typography variant='body1'>
            You will receive access to your digital images with your book or mosaic.
          </Typography>
        </Box>
        {digitalBullets.map(({ text }, index) => (
          <ProductDescriptionItem key={index}>{text}</ProductDescriptionItem>
        ))}
        <Box className={classes.priceInfoBox}>
          <Typography variant='body1'>
            <strong>
              Starting at <ProductPriceTypography>${MIN_PRICE}</ProductPriceTypography> for {PRICING.books.minPages} Art
              Pieces
            </strong>
          </Typography>
          <Typography variant='caption'>*Price doesn’t include Artkive Box</Typography>
        </Box>
        <Box display='flex' alignItems='center' justifyContent={isMobile ? 'center' : 'left'} mt={4}>
          <TrackButton
            color='primary'
            href={boxCheckoutUrl}
            size='large'
            variant='contained'
            trackerEvent={GET_BOX_CLICK}
            trackerOptions={{
              section: 'product_description_section',
            }}
          >
            Get My Box
          </TrackButton>
        </Box>
      </ProductDescriptionSection>
      <ContainedSection bgcolor={isMobile ? theme.colors.sky[300] : 'transparent'}>
        <Typography variant='h2' align='center' id='slider' ref={sliderRef}>
          How Much Does Digitization Cost?
        </Typography>
        <Box textAlign={isMobile ? 'left' : 'center'} mt={3} mb={3} px={isMobile ? 0 : 30}>
          <Typography variant='body1'>Cost is dependent on number of art pieces sent with Artkive Box.</Typography>
        </Box>
        <Box textAlign={isMobile ? 'left' : 'center'} mb={4} px={isMobile ? 0 : 30}>
          <Typography variant='body1'>
            If you have more than {MAX_AMOUNT_OF_PICS} pieces please{' '}
            <strong>
              <Link href={`mailto:${HELP_EMAIL}`}>email us</Link>
            </strong>{' '}
            for a quote.
          </Typography>
        </Box>
        <Box display='flex' justifyContent='center' mt={isMobile ? 5 : 8}>
          <ValueLabelComponent value={sliderValue} />
        </Box>
        <Box px={isMobile ? 1 : 14} mt={isMobile ? 5 : 4} mb={isMobile ? 5 : 8}>
          <PriceSlider
            value={sliderValue}
            min={MIN_AMOUNT_OF_PICS}
            max={MAX_AMOUNT_OF_PICS}
            trackerEvent={DIGITAL_STORAGE.SLIDER_INTERACTION}
            onChange={handleSliderChange}
          />
        </Box>
      </ContainedSection>
      <ContainedSection>
        <Testimonial
          imageSrc={TestimonialImage}
          quote="I was planning to photograph all the artwork myself, but just didn't have the time. I'm so happy
                  I used Artkive instead! I love having such professional, high-quality pics of the artwork to easily
                  send our other family members and include in family photo albums."
          user={testimonialUser}
        />
      </ContainedSection>
    </div>
  )
}

export default withApp(Digital)
